import { Alert, Button, Divider, Form, Input } from 'antd';
import { useLoginMutation } from 'api/AuthApi';
import AuthComponent from 'components/AuthComponent/AuthComponent';
import SocialLogin from 'pages/Auth/SocialLogin/SocialLogin';
import { Link, useSearchParams } from 'react-router-dom';
import { logging, validateForm } from 'utils/utils';
import './Login.css';

const getRetryAfterSeconds = (error: any): number | null => {
  const retryAfter = error?.meta?.response?.headers?.get?.('Retry-After');
  if (!retryAfter) {
    return null;
  }

  const parsed = Number(retryAfter);
  if (!Number.isNaN(parsed) && parsed > 0) {
    return parsed;
  }

  const retryDate = Date.parse(retryAfter);
  if (Number.isNaN(retryDate)) {
    return null;
  }

  const secondsUntilRetry = Math.ceil((retryDate - Date.now()) / 1000);
  return secondsUntilRetry > 0 ? secondsUntilRetry : null;
};

const getLoginErrorMessage = (error: any): string => {
  const status = error?.status || error?.originalStatus;

  if (status === 400 || status === 401) {
    return 'Invalid email or password';
  }

  if (status === 429) {
    const retryAfterSeconds = getRetryAfterSeconds(error);
    if (retryAfterSeconds) {
      const waitMinutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
      return `Too many login attempts. Please wait about ${waitMinutes} minute${waitMinutes > 1 ? 's' : ''} and try again.`;
    }

    return 'Too many login attempts. Please wait a few minutes and try again.';
  }

  return 'Unable to sign in right now. Please try again.';
};

function Login() {
  const [searchParams] = useSearchParams();
  const [login, { isError, error: errorLogin, isLoading }] = useLoginMutation();
  const [form] = Form.useForm();

  const onSubmit = async (values: { email: string; password: string }) => {
    if (isLoading) {
      return;
    }

    try {
      await login({
        email: values.email,
        password: values.password
      }).unwrap();
    } catch (e) {
      logging(e);
      validateForm(form, e);
    }
  };

  return (
    <AuthComponent
      title="Sign In"
      subtitle={
        <>
          Don't have an account? <Link to="/signup">Register Here</Link>
        </>
      }
    >
      <div className="login">
        <Form
          name="basic"
          layout="vertical"
          onFinish={onSubmit}
          initialValues={{
            email: searchParams.get('email')
          }}
          autoComplete="off"
          requiredMark="optional"
          form={form}
        >
          {isError && <Alert className="message" message={getLoginErrorMessage(errorLogin)} type="error" showIcon />}
          {searchParams.get('confirm') && (
            <Alert message="Your email was confirmed. Please login now." type="success" showIcon />
          )}
          {searchParams.get('reset') && (
            <Alert message="Your password was reset. Please login now." type="success" showIcon />
          )}

          <Form.Item label="Email" name="email" validateTrigger="onBlur" rules={[{ required: true }, { type: 'email' }]}>
            <Input size="large" placeholder="email@domain.com" />
          </Form.Item>

          <Form.Item label="Password" name="password" rules={[{ required: true }]}>
            <Input.Password size="large" autoComplete="current-password" />
          </Form.Item>

          <div className="forgot-password">
            <Link to="/forgot-password">Forgot Password?</Link>
          </div>

          <Form.Item>
            <Button
              block
              type="primary"
              htmlType="submit"
              className="submit-button"
              loading={isLoading}
              disabled={isLoading}
            >
              Sign In
            </Button>
          </Form.Item>

          <Divider plain>or</Divider>

          <SocialLogin />
        </Form>
      </div>
    </AuthComponent>
  );
}

export default Login;
